import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Chip
} from '@mui/material';
import { CheckCircle, Error, NetworkCheck } from '@mui/icons-material';
import axios from 'axios';
import { hostname } from '../../../utils';

interface ImapIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    imapHost?: string;
    imapPort?: number;
    smtpHost?: string;
    smtpPort?: number;
    email?: string;
    password?: string;
    useSsl?: boolean;
    useTls?: boolean;
    folder?: string;
    syncFrequency?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
  onSubmitAttempt?: boolean;
  getToken: () => Promise<string>;
  integrationId?: string;
}

const ImapIntegrationForm: React.FC<ImapIntegrationFormProps> = ({
  editMode = false,
  initialData = {},
  onValidationChange,
  onDataChange,
  onSubmitAttempt,
  getToken,
  integrationId,
}) => {
  const [formData, setFormData] = useState({
    imapHost: initialData.imapHost || '',
    imapPort: initialData.imapPort || 993,
    smtpHost: initialData.smtpHost || '',
    smtpPort: initialData.smtpPort || 587,
    email: initialData.email || '',
    password: initialData.password || '',
    useSsl: initialData.useSsl !== undefined ? initialData.useSsl : true,
    useTls: initialData.useTls !== undefined ? initialData.useTls : true,
    folder: initialData.folder || 'INBOX',
    syncFrequency: initialData.syncFrequency || '5min',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  // Connection test state
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean;
    results?: {
      imap: boolean;
      smtp: boolean;
      error?: string;
    };
    error?: string;
  } | null>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.imapHost.trim()) {
      newErrors.imapHost = 'IMAP host is required';
    }

    if (!formData.smtpHost.trim()) {
      newErrors.smtpHost = 'SMTP host is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (formData.imapPort < 1 || formData.imapPort > 65535) {
      newErrors.imapPort = 'IMAP port must be between 1 and 65535';
    }

    if (formData.smtpPort < 1 || formData.smtpPort > 65535) {
      newErrors.smtpPort = 'SMTP port must be between 1 and 65535';
    }

    if (!formData.folder.trim()) {
      newErrors.folder = 'Folder name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    // Only validate for the parent component, don't show errors yet
    const newErrors: Record<string, string> = {};

    if (!formData.imapHost.trim()) {
      newErrors.imapHost = 'IMAP host is required';
    }

    if (!formData.smtpHost.trim()) {
      newErrors.smtpHost = 'SMTP host is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (formData.imapPort < 1 || formData.imapPort > 65535) {
      newErrors.imapPort = 'IMAP port must be between 1 and 65535';
    }

    if (formData.smtpPort < 1 || formData.smtpPort > 65535) {
      newErrors.smtpPort = 'SMTP port must be between 1 and 65535';
    }

    if (!formData.folder.trim()) {
      newErrors.folder = 'Folder name is required';
    }

    const isValid = Object.keys(newErrors).length === 0;
    onValidationChange(isValid);
    onDataChange(formData);
  }, [formData, onValidationChange, onDataChange]);

  // Handle submit attempt - show all validation errors
  useEffect(() => {
    if (onSubmitAttempt) {
      setSubmitAttempted(true);
      // Mark all fields as touched to show validation errors
      setTouched({
        imapHost: true,
        smtpHost: true,
        email: true,
        password: true,
        imapPort: true,
        smtpPort: true,
        folder: true,
      });
      validateForm();
    }
  }, [onSubmitAttempt]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Mark field as touched when user interacts with it
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    // Clear test results when connection settings change
    if (['imapHost', 'smtpHost', 'email', 'password', 'imapPort', 'smtpPort', 'useSsl', 'useTls'].includes(field)) {
      setConnectionTestResult(null);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    // Run validation and update errors when field loses focus
    validateForm();
  };

  const getCommonEmailProviders = () => {
    return [
      { name: 'Gmail', imap: 'imap.gmail.com', smtp: 'smtp.gmail.com', imapPort: 993, smtpPort: 587 },
      { name: 'Outlook/Hotmail', imap: 'outlook.office365.com', smtp: 'smtp.office365.com', imapPort: 993, smtpPort: 587 },
      { name: 'Yahoo', imap: 'imap.mail.yahoo.com', smtp: 'smtp.mail.yahoo.com', imapPort: 993, smtpPort: 587 },
      { name: 'iCloud', imap: 'imap.mail.me.com', smtp: 'smtp.mail.me.com', imapPort: 993, smtpPort: 587 },
    ];
  };

  const handleProviderSelect = (provider: any) => {
    setFormData(prev => ({
      ...prev,
      imapHost: provider.imap,
      smtpHost: provider.smtp,
      imapPort: provider.imapPort,
      smtpPort: provider.smtpPort,
    }));
    // Clear previous test results when settings change
    setConnectionTestResult(null);
  };

  const testConnection = async () => {
    // Validate required fields for connection test
    const requiredFields = ['imapHost', 'smtpHost', 'email', 'password'];
    const hasAllFields = requiredFields.every(field => formData[field as keyof typeof formData]?.toString().trim());
    
    if (!hasAllFields) {
      setConnectionTestResult({
        success: false,
        error: 'Please fill in all required fields (IMAP Host, SMTP Host, Email, and Password) before testing connection.'
      });
      return;
    }

    setIsTestingConnection(true);
    setConnectionTestResult(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/imap/test-connection`,
        {
          imapHost: formData.imapHost,
          imapPort: formData.imapPort,
          smtpHost: formData.smtpHost,
          smtpPort: formData.smtpPort,
          email: formData.email,
          password: formData.password,
          useSsl: formData.useSsl,
          useTls: formData.useTls,
          // Pass integrationId so backend can reset status on successful test
          ...(integrationId && { integrationId }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setConnectionTestResult({
        success: response.data.success,
        results: response.data.results,
      });
    } catch (error: any) {
      setConnectionTestResult({
        success: false,
        error: error.response?.data?.error || 'Connection test failed. Please check your settings and try again.',
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        IMAP Email Configuration
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure your IMAP email account to sync emails and send messages. For Gmail and other providers, 
        you may need to use an App Password instead of your regular password.
        <br />
        <strong>For Gmail users:</strong> You must create an App Password. 
        <a 
          href="https://support.google.com/accounts/answer/185833?hl=en" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          Click here to learn how to create an App Password
        </a>.
      </Alert>

      {/* Quick Setup for Common Providers */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Setup for Common Providers:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {getCommonEmailProviders().map((provider) => (
            <Box
              key={provider.name}
              onClick={() => handleProviderSelect(provider)}
              sx={{
                p: 1,
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              <Typography variant="caption">{provider.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Email Account Settings */}
      <Typography variant="subtitle1" gutterBottom>
        Email Account
      </Typography>
      
      <TextField
        fullWidth
        label="Email Address"
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        onBlur={() => handleBlur('email')}
        error={!!(errors.email && (touched.email || submitAttempted))}
        helperText={(errors.email && (touched.email || submitAttempted)) ? errors.email : 'The email address for this IMAP account'}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={formData.password}
        onChange={(e) => handleChange('password', e.target.value)}
        onBlur={() => handleBlur('password')}
        error={!!(errors.password && (touched.password || submitAttempted))}
        helperText={(errors.password && (touched.password || submitAttempted)) ? errors.password : 'Account password or App Password for the email account'}
        required
        sx={{ mb: 2 }}
      />

      {/* Connection Test Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={isTestingConnection ? <CircularProgress size={20} /> : <NetworkCheck />}
            onClick={testConnection}
            disabled={isTestingConnection}
            size="small"
          >
            {isTestingConnection ? 'Testing...' : 'Test Connection'}
          </Button>
          
          {connectionTestResult && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {connectionTestResult.success ? (
                <Chip
                  icon={<CheckCircle />}
                  label="Connection Successful"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<Error />}
                  label="Connection Failed"
                  color="error"
                  size="small"
                />
              )}
            </Box>
          )}
        </Box>

        {connectionTestResult && (
          <Box sx={{ mt: 1 }}>
            {connectionTestResult.success && connectionTestResult.results ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={connectionTestResult.results.imap ? <CheckCircle /> : <Error />}
                  label={`IMAP: ${connectionTestResult.results.imap ? 'Connected' : 'Failed'}`}
                  color={connectionTestResult.results.imap ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={connectionTestResult.results.smtp ? <CheckCircle /> : <Error />}
                  label={`SMTP: ${connectionTestResult.results.smtp ? 'Connected' : 'Failed'}`}
                  color={connectionTestResult.results.smtp ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            ) : (
              <Alert severity="error" sx={{ mt: 1 }}>
                {connectionTestResult.error || 'Connection test failed'}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* IMAP Server Settings */}
      <Typography variant="subtitle1" gutterBottom>
        Incoming Mail (IMAP) Settings
      </Typography>

      <TextField
        fullWidth
        label="IMAP Host"
        value={formData.imapHost}
        onChange={(e) => handleChange('imapHost', e.target.value)}
        onBlur={() => handleBlur('imapHost')}
        error={!!(errors.imapHost && (touched.imapHost || submitAttempted))}
        helperText={(errors.imapHost && (touched.imapHost || submitAttempted)) ? errors.imapHost : 'IMAP server hostname (e.g., imap.gmail.com)'}
        required
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="IMAP Port"
          type="number"
          value={formData.imapPort}
          onChange={(e) => handleChange('imapPort', parseInt(e.target.value) || 993)}
          onBlur={() => handleBlur('imapPort')}
          error={!!(errors.imapPort && (touched.imapPort || submitAttempted))}
          helperText={(errors.imapPort && (touched.imapPort || submitAttempted)) ? errors.imapPort : 'Usually 993 for SSL'}
          required
          sx={{ flex: 1 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.useSsl}
              onChange={(e) => handleChange('useSsl', e.target.checked)}
            />
          }
          label="Use SSL"
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        fullWidth
        label="Mail Folder"
        value={formData.folder}
        onChange={(e) => handleChange('folder', e.target.value)}
        onBlur={() => handleBlur('folder')}
        error={!!(errors.folder && (touched.folder || submitAttempted))}
        helperText={(errors.folder && (touched.folder || submitAttempted)) ? errors.folder : 'Email folder to sync from (usually INBOX)'}
        required
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 3 }} />

      {/* SMTP Server Settings */}
      <Typography variant="subtitle1" gutterBottom>
        Outgoing Mail (SMTP) Settings
      </Typography>

      <TextField
        fullWidth
        label="SMTP Host"
        value={formData.smtpHost}
        onChange={(e) => handleChange('smtpHost', e.target.value)}
        onBlur={() => handleBlur('smtpHost')}
        error={!!(errors.smtpHost && (touched.smtpHost || submitAttempted))}
        helperText={(errors.smtpHost && (touched.smtpHost || submitAttempted)) ? errors.smtpHost : 'SMTP server hostname (e.g., smtp.gmail.com)'}
        required
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          label="SMTP Port"
          type="number"
          value={formData.smtpPort}
          onChange={(e) => handleChange('smtpPort', parseInt(e.target.value) || 587)}
          onBlur={() => handleBlur('smtpPort')}
          error={!!(errors.smtpPort && (touched.smtpPort || submitAttempted))}
          helperText={(errors.smtpPort && (touched.smtpPort || submitAttempted)) ? errors.smtpPort : 'Usually 587 for TLS'}
          required
          sx={{ flex: 1 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.useTls}
              onChange={(e) => handleChange('useTls', e.target.checked)}
            />
          }
          label="Use TLS"
          sx={{ flex: 1 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Sync Settings */}
      <Typography variant="subtitle1" gutterBottom>
        Sync Settings
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Sync Frequency</InputLabel>
        <Select
          value={formData.syncFrequency}
          label="Sync Frequency"
          onChange={(e) => handleChange('syncFrequency', e.target.value)}
        >
          <MenuItem value="5min">Every 5 minutes</MenuItem>
          <MenuItem value="hourly">Hourly</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default ImapIntegrationForm;